import { useEffect } from 'react';
import SSEConsumer from '@shared/services/sse-consumer';
import { IOrder, OrderStatus, useOrdersActions } from '../context/orders';
import {
  CourierReturnStatusType,
  ICourier,
  ICourierReturnStatus,
  useCouriersActions,
  useCouriersState,
} from '../context/couriers';
import { useKeycloakUserState } from '@src/shared/context/keycloak-user';
import {
  NotificationCategories,
  NotificationType,
  useNotificationsActions,
} from '@src/shared/context/notifications';
import { useTranslation } from 'react-i18next';
import { DrawerType, useInsideDrawerState } from '@src/shared/context/inside-drawer';

enum DeliveriesServerEventType {
  OrderListSnapshot = 'OrderListSnapshot',
  OrderCreated = 'OrderCreated',
  OrderUpdated = 'OrderUpdated',
  OrderExpired = 'OrderExpired',
  CourierListSnapshot = 'CourierListSnapshot',
  CourierStatusListSnapshot = 'CourierStatusListSnapshot',
  CourierStatusUpdated = 'CourierStatusUpdated',
  CourierUpdated = 'CourierUpdated',
  CourierLocationUpdated = 'CourierLocationUpdated',
  RouteListSnapshot = 'RouteListSnapshot',
  RouteUpdated = 'RouteUpdated',
  EtaListSnapshot = 'EtaListSnapshot',
  EtaUpdated = 'EtaUpdated',
  CourierDeleted = 'CourierDeleted',
  NextRouteUpdated = 'NextRouteUpdated',
  NextRouteListSnapshot = 'NextRouteListSnapshot',
}

const useDeliveriesEventSource = (SSEerror = false) => {
  const { notify } = useNotificationsActions(NotificationCategories.Dashboard);
  const { activeDrawerState } = useInsideDrawerState();
  const { addOrder, updateOrder, setOrders, expiredOrder } = useOrdersActions();
  const { restaurant, keycloakToken } = useKeycloakUserState();
  const { t } = useTranslation();
  const {
    addCourier,
    setCouriers,
    updateCourier,
    updateCourierLocation,
    updateCourierRoute,
    setCourierRoutes,
    setCourierStatus,
    updateCourierStatus,
    setEtaLists,
    updateEta,
    deleteCourier,
    updateNextRoute,
    setNextRoute,
  } = useCouriersActions();
  const getNowStamp = new Date().getTime();
  const { couriersById } = useCouriersState();

  const setSSE = (usersId?: string, token?: string) => {
    return new SSEConsumer(process.env.DELIVERIES_SSE_SOURCE_URL as string, token, usersId);
  };

  const notifyOrderUpdate = (order: IOrder) => {
    //Temporary solution for spam notifications
    if (
      order.status === OrderStatus.Assigned ||
      order.status === OrderStatus.Unassigned ||
      order.status === OrderStatus.AssignedToNextPool ||
      order.status === OrderStatus.InDelivery ||
      order.status === OrderStatus.Delivered
    ) {
      return;
    }

    const courierName = `${order.courier?.firstName || ''} ${order.courier?.lastName || ''}`;
    const selectedNotificationType = {
      [OrderStatus.Assigned]: NotificationType.Success,
      [OrderStatus.Unassigned]: NotificationType.Warning,
    };
    const selectedNotificationTitle = {
      [OrderStatus.Assigned]: t('notification_headers.orderAssignSuccessHeader'),
      [OrderStatus.Unassigned]: t('notification_headers.orderUnassignSuccessHeader'),
    };
    const selectedNotificationMessage = {
      [OrderStatus.Assigned]: t(`graphql.SUCCESS_ORDER_ASSIGN`, {
        orderReferance: order.reference,
        courierName,
      }),
      [OrderStatus.Unassigned]: t(`graphql.SUCCESS_ORDER_UNASSIGN`, {
        courierName,
        orderId: order.reference,
      }),
    };

    notify({
      message: selectedNotificationMessage[order.status],
      title: selectedNotificationTitle[order.status],
      type: selectedNotificationType[order.status],
      showOnlyInDrawer: true,
      seen: activeDrawerState === DrawerType.NOTIFICATION,
    });
  };

  const notifyCourierUpdate = (updatedInfo: ICourierReturnStatus) => {
    if (updatedInfo.status === CourierReturnStatusType.Returning) {
      const courier: ICourier = couriersById[updatedInfo.id];
      if (courier) {
        const courierName = `${courier?.firstName || ''} ${courier?.lastName || ''}`;
        notify({
          message: t('graphql.SUCCESS_COURIER_RETURN', { Courier_Name: courierName }),
          title: t('notification_headers.courierReturnHeader'),
          type: NotificationType.Information,
          showOnlyInDrawer: true,
          seen: activeDrawerState === DrawerType.NOTIFICATION,
        });
      }
    }
  };

  let snapshotActiveOrders: any[] = [];
  useEffect(() => {
    if (!keycloakToken || !restaurant?.id) {
      return;
    }

    const consumer = setSSE(restaurant.id, keycloakToken);

    consumer.subscribeToEvent(DeliveriesServerEventType.OrderListSnapshot, (data: IOrder[]) => {
      data.map((order) => snapshotActiveOrders.push(order.uuid));
      setOrders(Object.assign(data, { expireTime: getNowStamp }));
    });

    consumer.subscribeToEvent(DeliveriesServerEventType.OrderCreated, (data: IOrder) => {
      addOrder(data);
      if (data.courier) {
        addCourier(data.courier);
      }
    });

    consumer.subscribeToEvent(DeliveriesServerEventType.OrderUpdated, (data: IOrder) => {
      updateOrder(data);
      notifyOrderUpdate(data);
      if (data.courier) {
        addCourier(data.courier);
      }
    });

    consumer.subscribeToEvent(DeliveriesServerEventType.OrderExpired, expiredOrder);

    consumer.subscribeToEvent(DeliveriesServerEventType.CourierListSnapshot, (data: ICourier) => {
      setCouriers(data);
    });

    consumer.subscribeToEvent(DeliveriesServerEventType.CourierUpdated, updateCourier);

    consumer.subscribeToEvent(
      DeliveriesServerEventType.CourierLocationUpdated,
      updateCourierLocation
    );

    consumer.subscribeToEvent(
      DeliveriesServerEventType.CourierStatusListSnapshot,
      setCourierStatus
    );

    consumer.subscribeToEvent(
      DeliveriesServerEventType.CourierStatusUpdated,
      (status: ICourierReturnStatus) => {
        updateCourierStatus(status);
        notifyCourierUpdate(status);
      }
    );

    consumer.subscribeToEvent(DeliveriesServerEventType.RouteListSnapshot, (data) => {
      setCourierRoutes(data, snapshotActiveOrders);
    });

    consumer.subscribeToEvent(DeliveriesServerEventType.RouteUpdated, updateCourierRoute);

    consumer.subscribeToEvent(DeliveriesServerEventType.EtaListSnapshot, setEtaLists);

    consumer.subscribeToEvent(DeliveriesServerEventType.CourierDeleted, deleteCourier);

    consumer.subscribeToEvent(DeliveriesServerEventType.EtaUpdated, updateEta);

    consumer.subscribeToEvent(DeliveriesServerEventType.NextRouteUpdated, updateNextRoute);

    consumer.subscribeToEvent(DeliveriesServerEventType.NextRouteListSnapshot, (data) => {
      if (data.length) {
        setNextRoute(data[0]);
      }
    });

    return () => {
      consumer.disconnect();
    };
  }, [activeDrawerState === DrawerType.NOTIFICATION, keycloakToken, restaurant?.id]);
};

export default useDeliveriesEventSource;
